import { memo, useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import { alpha, useTheme } from "@mui/material/styles";

import ClinicalCrmContextInfoBanner from "../../messageTemplates/components/ClinicalCrmContextInfoBanner";
import MessageTemplatePhonePreviewContent from "../../messageTemplates/components/MessageTemplatePhonePreviewContent";
import type { MessageTemplate } from "../../messageTemplates/types";
import {
  formatTemplateDisplayName,
  getStatusUi,
  isMessageTemplateApproved,
  templateStatusChipSx,
} from "../../messageTemplates/utils/messageTemplateUiUtils";
import type { QuestionnairePayload } from "src-next/modules/questionnaire-builder/form-builder/types";
import { resolveQuestionnaireIdFromTemplate } from "../helpers/automationTemplateUtils";
import AutomationLinkedQuestionnairePreview from "./AutomationLinkedQuestionnairePreview";

export interface AutomationTemplatePickerProps {
  templates: MessageTemplate[];
  organizationId: string;
  selectedTemplateId: string;
  onSelectTemplate: (templateId: string) => void;
  questionnaires: QuestionnairePayload[];
}

const AutomationTemplatePicker = memo(
  ({
    templates,
    organizationId,
    selectedTemplateId,
    onSelectTemplate,
    questionnaires,
  }: AutomationTemplatePickerProps) => {
    const theme = useTheme();
    const [searchQuery, setSearchQuery] = useState("");
    const selectedListItemRef = useRef<HTMLButtonElement | null>(null);

    const filteredTemplates = useMemo(() => {
      const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
      if (!normalizedQuery) return templates;

      return templates.filter((template) => {
        const displayName = formatTemplateDisplayName(template.name, {
          organizationPrefix: organizationId,
        });
        return displayName.toLocaleLowerCase().includes(normalizedQuery);
      });
    }, [organizationId, searchQuery, templates]);

    const selectedTemplate = useMemo(
      () => templates.find((template) => template.id === selectedTemplateId),
      [selectedTemplateId, templates],
    );

    const linkedQuestionnaire = useMemo(() => {
      const questionnaireId = resolveQuestionnaireIdFromTemplate(selectedTemplate);
      if (!questionnaireId) return undefined;
      return questionnaires.find((item) => item.id === questionnaireId);
    }, [questionnaires, selectedTemplate]);

    const handleTemplateClick = (template: MessageTemplate) => {
      if (!isMessageTemplateApproved(template.status)) return;

      // Toggle: clicar no mesmo modelo remove a seleção.
      if (selectedTemplateId === template.id) {
        onSelectTemplate("");
        return;
      }

      onSelectTemplate(template.id);
    };

    useEffect(() => {
      if (!selectedTemplateId) return;
      selectedListItemRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }, [selectedTemplateId]);

    if (templates.length === 0) {
      return (
        <Paper
          variant="outlined"
          sx={{ py: 4, px: 2, borderRadius: 2, textAlign: "center" }}
        >
          <Typography variant="body2" color="text.secondary">
            Nenhum modelo personalizado disponível. Crie um modelo na aba
            &quot;Modelos&quot; (fora dos modelos padrão) antes de configurar
            esta automação.
          </Typography>
        </Paper>
      );
    }

    return (
      <Stack spacing={1.5}>
        <TextField
          fullWidth
          size="small"
          label="Buscar modelo"
          variant="filled"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
        />

        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", md: "minmax(220px, 34%) 1fr" },
            minHeight: { md: 420 },
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              maxHeight: { xs: 280, md: "none" },
            }}
          >
            <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="caption" color="text.secondary">
                Modelos da organização ({filteredTemplates.length})
              </Typography>
            </Box>

            <Stack
              spacing={0}
              sx={{
                overflowY: "auto",
                flex: 1,
                p: 0.75,
              }}
            >
              {filteredTemplates.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, px: 1, textAlign: "center" }}
                >
                  Nenhum modelo encontrado.
                </Typography>
              ) : (
                filteredTemplates.map((template) => {
                  const isSelected = selectedTemplateId === template.id;
                  const isApproved = isMessageTemplateApproved(template.status);
                  const statusUi = getStatusUi(template.status);
                  const displayName = formatTemplateDisplayName(template.name, {
                    organizationPrefix: organizationId,
                  });

                  return (
                    <Box
                      key={template.id}
                      component="button"
                      type="button"
                      ref={isSelected ? selectedListItemRef : undefined}
                      onClick={() => handleTemplateClick(template)}
                      disabled={!isApproved}
                      aria-pressed={isSelected}
                      sx={{
                        width: "100%",
                        textAlign: "left",
                        border: "1px solid",
                        borderColor: isSelected ? "success.main" : "divider",
                        borderRadius: 1.5,
                        bgcolor: isSelected
                          ? alpha(theme.palette.success.main, 0.08)
                          : "background.paper",
                        p: 1,
                        mb: 0.75,
                        cursor: isApproved ? "pointer" : "not-allowed",
                        opacity: isApproved ? 1 : 0.6,
                        transition: "border-color 0.15s ease, background-color 0.15s ease",
                        "&:hover": isApproved
                          ? {
                              borderColor: isSelected ? "success.main" : "primary.main",
                              bgcolor: isSelected
                                ? alpha(theme.palette.success.main, 0.12)
                                : alpha(theme.palette.primary.main, 0.04),
                            }
                          : undefined,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {displayName}
                          </Typography>
                          <Chip
                            label={statusUi.label}
                            color={statusUi.color}
                            size="small"
                            variant="outlined"
                            sx={{ mt: 0.5, ...templateStatusChipSx }}
                          />
                        </Box>
                        {isSelected ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : null}
                      </Stack>
                    </Box>
                  );
                })
              )}
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              minHeight: { md: 420 },
            }}
          >
            {!selectedTemplate ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ flex: 1, py: 4, textAlign: "center" }}
                spacing={1}
              >
                <InfoOutlinedIcon color="disabled" />
                <Typography variant="body2" color="text.secondary">
                  Selecione um modelo à esquerda para visualizar o preview e o
                  questionário vinculado.
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="subtitle1" fontWeight={600}>
                    {formatTemplateDisplayName(selectedTemplate.name, {
                      organizationPrefix: organizationId,
                    })}
                  </Typography>
                  <Chip
                    label={getStatusUi(selectedTemplate.status).label}
                    color={getStatusUi(selectedTemplate.status).color}
                    size="small"
                    variant="outlined"
                    sx={templateStatusChipSx}
                  />
                </Stack>

                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  <MessageTemplatePhonePreviewContent
                    isCard={false}
                    header={selectedTemplate.header}
                    body={selectedTemplate.preview}
                    hasButton={Boolean(
                      selectedTemplate.buttonType &&
                        String(selectedTemplate.buttonText ?? "").trim(),
                    )}
                    buttonText={selectedTemplate.buttonText ?? ""}
                    buttonType={selectedTemplate.buttonType ?? ""}
                    footerTime={dayjs(selectedTemplate.createdAtIso).format("HH:mm")}
                  />
                </Box>

                {linkedQuestionnaire ? (
                  <AutomationLinkedQuestionnairePreview
                    questionnaire={linkedQuestionnaire}
                  />
                ) : (
                  <ClinicalCrmContextInfoBanner
                    variant="info"
                    title="Sem questionário vinculado"
                    icon={<InfoOutlinedIcon />}
                    description="Este modelo não possui questionário vinculado."
                  />
                )}
              </Stack>
            )}
          </Paper>
        </Box>
      </Stack>
    );
  },
);

AutomationTemplatePicker.displayName = "AutomationTemplatePicker";

export default AutomationTemplatePicker;
